import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEmployees } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import type { HrLevel } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { EmployeeDialog } from "./EmployeeDialog";
import { dateOnly, StatusPill, Table, useEmpName } from "./shared";

export function PeoplePanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = useEmpName();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const emps = useEmployees({ q: q.trim() || undefined, status: status || undefined });
  const [open, setOpen] = useState<{ id: string | null } | null>(null);

  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.people")}>
      <SectionHeader icon="👥" title={t("hr.tabs.people")} subtitle={t("hr.people.subtitle")}
        right={<div className="flex flex-wrap items-center gap-2">
          <input className="field !w-44" placeholder={t("hr.people.search")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("hr.people.search")} />
          <select className="field !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("hr.status.label")}>
            <option value="">{t("hr.all")}</option>
            {["active", "on_notice", "suspended", "terminated"].map((s) => <option key={s} value={s}>{t(`hr.status.${s}`)}</option>)}
          </select>
          {level >= 2 && <button type="button" className="btn-primary" onClick={() => setOpen({ id: null })}>{t("hr.people.add")}</button>}
        </div>} />
      <Table head={[t("hr.employeeNo"), t("hr.employee"), t("hr.jobTitle"), t("hr.hired"), t("hr.status.label")]} empty={(emps.data ?? []).length === 0 ? <Empty>{t("hr.people.none")}</Empty> : undefined}>
        {(emps.data ?? []).map((e) => (
          <tr key={e.id} className="cursor-pointer hover:bg-purple-50/40" onClick={() => setOpen({ id: e.id })}>
            <td className="num font-mono font-bold">{e.employee_no}</td>
            <td className="font-black">{name(e)}</td>
            <td>{locale === "ar" && e.job_title_ar ? e.job_title_ar : e.job_title ?? "—"}</td>
            <td className="num">{dateOnly(e.hire_date, locale)}</td>
            <td><StatusPill status={e.status} /></td>
          </tr>
        ))}
      </Table>
      {open && <EmployeeDialog id={open.id} open level={level} onClose={() => setOpen(null)} />}
    </section>
  );
}
